import React, { useState, useEffect, useRef } from "react";
import { Column, Row, Text, Button, Icon, Badge, Thumbnail } from "@amzn/meridian";
import cameraTokens from "@amzn/meridian-tokens/base/icon/camera";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import FlashlightButton from "../../inspection/components/FlashlightButton";
import ZoomControl from "../../inspection/components/ZoomControl";
import { captureVideoFrame } from "../../inspection/utils/videoUtils";
import { useCamera } from "src/hooks/useCamera";
import { useAppDispatch, useAppSelector } from "src/store/store";
import { analyzeObjectPresenceAsync } from "src/store/inspectionResultsSlice";
import { CapturedImage } from "src/types/classificationTypes";
import ImagePreviewOverlay from "../../classification/ImagePreviewOverlay";
import PillButton from "src/components/UI/PillButton";
import StepTransitionSheet from "./StepTransitionSheet";
import "src/components/UI/cameraOverlay.scss";
import "./SimpleCamera.scss";

interface ManualCaptureMultipleStepProps {
  humanText: string;
  activeQuestionIds: string[];
  onComplete?: () => void;
  onCancel?: () => void;
  onSubmitFiles?: (files: File[]) => void;
  // Optional cap on how many images can be captured. Omitted (undefined) = unlimited,
  // the existing behavior for every current caller. The blur-retake flow passes 1 so the
  // inspector captures exactly one replacement photo.
  maxImages?: number;
}

const createIdempotencyKey = (): string => {
  const values = new Uint32Array(4);
  window.crypto.getRandomValues(values);
  return Array.from(values, (value) => value.toString(16).padStart(8, "0")).join("");
};

const ManualCaptureMultipleStep: React.FC<ManualCaptureMultipleStepProps> = ({
  humanText,
  activeQuestionIds,
  onComplete,
  onCancel,
  onSubmitFiles,
  maxImages,
}) => {
  const { videoRef, isReady, error, setError } = useCamera();
  const dispatch = useAppDispatch();
  const { inspectionId } = useAppSelector((state) => state.workflow);
  const [capturedImages, setCapturedImages] = useState<CapturedImage[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [stepComplete, setStepComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const idempotencyKeyRef = useRef(createIdempotencyKey());
  const activeQuestionKey = activeQuestionIds.join("|");

  // Reset step state when the active step changes.
  useEffect(() => {
    setStepComplete(false);
    setCapturedImages([]);
    setIsSubmitting(false);
    isSubmittingRef.current = false;
    idempotencyKeyRef.current = createIdempotencyKey();
  }, [humanText, activeQuestionKey]);

  // Keep a ref so the cleanup closure always has the latest list
  const capturedImagesRef = useRef<CapturedImage[]>([]);
  capturedImagesRef.current = capturedImages;

  // Revoke blob URLs on unmount. This is to handle the edge case where the user navigates
  // away from the page without Submitting the images.
  useEffect(() => {
    return () => {
      capturedImagesRef.current.forEach((img) => URL.revokeObjectURL(img.displayUrl));
    };
  }, []);

  // When maxImages is set, block further captures once the cap is reached (undefined =
  // unlimited, the existing behavior).
  const atMaxImages = maxImages !== undefined && capturedImages.length >= maxImages;

  const handleCapture = async () => {
    if (isSubmittingRef.current || !videoRef.current || !isReady || atMaxImages) {
      return;
    }
    try {
      const file = await captureVideoFrame(videoRef.current);
      if (isSubmittingRef.current) {
        return;
      }
      const displayUrl = URL.createObjectURL(file);
      idempotencyKeyRef.current = createIdempotencyKey();
      setCapturedImages((prev) => [...prev, { file, displayUrl }]);
    } catch {
      setError("Failed to capture frame. Please try again.");
    }
  };

  const handleDone = async () => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (onSubmitFiles) {
        const files = capturedImages.map((img) => img.file);
        capturedImages.forEach((img) => URL.revokeObjectURL(img.displayUrl));
        setCapturedImages([]);
        onSubmitFiles(files);
        return;
      }

      const payload = new FormData();
      payload.append("inspection_id", inspectionId);
      payload.append("idempotency_key", idempotencyKeyRef.current);
      activeQuestionIds.forEach((qId) => {
        payload.append("question_ids", qId);
      });
      capturedImages.forEach((img) => {
        payload.append("images", img.file);
      });

      await dispatch(analyzeObjectPresenceAsync(payload)).unwrap();
      capturedImages.forEach((img) => URL.revokeObjectURL(img.displayUrl));
      setCapturedImages([]);
      setStepComplete(true);
    } catch {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      setError("Failed to submit images. Please try again.");
    }
  };

  const handleDeleteImage = (index: number) => {
    if (isSubmittingRef.current) {
      return;
    }
    URL.revokeObjectURL(capturedImages[index].displayUrl);
    const updated = capturedImages.filter((_, i) => i !== index);
    idempotencyKeyRef.current = createIdempotencyKey();
    setCapturedImages(updated);
    if (updated.length === 0) {
      setShowPreview(false);
    }
  };

  return (
    <Column className="simple-camera-viewport pwa-fullscreen-viewport" height="100vh" spacing="none">
      {/* Live camera feed */}
      <video ref={videoRef} className="simple-camera-video" autoPlay muted playsInline />

      {onCancel && (
        <div
          className="simple-camera-cancel-wrapper"
          role="button"
          aria-label="Go back"
          onClick={() => {
            if (!isSubmittingRef.current) {
              onCancel();
            }
          }}
        >
          <Icon tokens={arrowLeftTokens} color="inverted" />
        </div>
      )}

      {error && (
        <div className="simple-camera-error">
          <Text color="error">{error}</Text>
        </div>
      )}

      {!isReady && !error && (
        <div className="simple-camera-loading">
          <Text color="inverted">Initializing camera...</Text>
        </div>
      )}

      {/* Top overlay: guidance text from humanText */}
      <Column className="camera-overlay camera-overlay--top" spacing="400" spacingInset="500">
        <div className="human-text-overlay">
          <Text color="inverted" alignment="center" className="human-text-overlay__text">
            {humanText}
          </Text>
        </div>
      </Column>

      {/* Bottom overlay: hint + capture controls + done button */}
      <Column className="camera-overlay camera-overlay--bottom" spacingInset="300 400" alignmentHorizontal="center" spacing="400">
        <Row className="camera-pill-bg" width="100%" alignmentHorizontal="center" spacingInset="300">
          <Text color="inverted" alignment="center">
            {atMaxImages ? "Tap SUBMIT to use this photo" : "Tap shutter button to capture images"}
          </Text>
        </Row>

        {/* Zoom control: positioned above capture button for easy thumb access */}
        <ZoomControl videoRef={videoRef} />

        {/* Camera controls row: thumbnail | capture button | flashlight */}
        <Row widths={["fill", "fill", "fill"]} alignmentVertical="center" width="100%">
          <Row alignmentHorizontal="start">
            {capturedImages.length > 0 && (
              <div
                className="capture-thumbnail__wrapper"
                onClick={() => {
                  if (!isSubmittingRef.current) {
                    setShowPreview(true);
                  }
                }}
              >
                <Thumbnail source={capturedImages[capturedImages.length - 1].displayUrl} size="medium" type="square" />
                <Row className="capture-thumbnail__badge">
                  <Badge value={capturedImages.length} />
                </Row>
              </div>
            )}
          </Row>

          <Row alignmentHorizontal="center">
            <div style={{ backgroundColor: "white", borderRadius: "50%" }}>
              <Button
                type="icon"
                size="xlarge"
                circular={true}
                onClick={handleCapture}
                disabled={!isReady || isSubmitting || atMaxImages}
              >
                <Icon tokens={cameraTokens} />
              </Button>
            </div>
          </Row>

          <Row alignmentHorizontal="end">
            <FlashlightButton videoRef={videoRef} />
          </Row>
        </Row>

        <Row spacingInset="300" minHeight="55px">
          {capturedImages.length > 0 && (
            <PillButton text="SUBMIT" color="success" onClick={handleDone} disabled={isSubmitting} />
          )}
        </Row>
      </Column>

      {showPreview && (
        <ImagePreviewOverlay
          images={capturedImages.map((img) => img.displayUrl)}
          onClose={() => setShowPreview(false)}
          onDelete={handleDeleteImage}
          onConfirm={() => {
            setShowPreview(false);
            handleDone();
          }}
          confirmButtonText="SUBMIT"
          disabled={isSubmitting}
        />
      )}

      {stepComplete && (
        <StepTransitionSheet
          headerMessage="Photos Captured!"
          message="Moving to next step."
          onClose={() => onComplete?.()}
        />
      )}
    </Column>
  );
};

export default ManualCaptureMultipleStep;
